import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useTranslations } from "../hooks/useTranslations";
import { useMe } from "../http/serviceportalApi";
import SVGOutbox from "../icons/Outbox.svg?react";
import SVGPeople from "../icons/People.svg?react";
import { Permission } from "../types";
import { includesAny } from "../utils";
import AccountButton from "./AccountButton";
import styles from "./AdminApp.module.css";
import Sidebar from "./Sidebar";
import AdminAccounting from "./accounting/AdminAccounting";
import AdminInvites from "./invites/AdminInvites";
import AppMenu from "./ui/AppMenu";
import Hamburger from "./ui/Hamburger";
import LoadingSpinner from "./ui/LoadingSpinner";
import Nav from "./ui/Nav";
import AdminUser from "./users/AdminUser";
import AdminUsers from "./users/AdminUsers";

const AdminApp = () => {
  const { i18n } = useTranslations();
  const auth = useAuth();
  const matchesMedia = useMediaQuery("(min-width: 1400px)");
  const [isSidebarOpen, setSidebarOpen] = useState(matchesMedia);

  const { data, isValidating } = useMe();
  const userPermissions = data?.permissions ?? [];

  const usersRoute = includesAny(userPermissions, [
    Permission.UsersRead,
    Permission.UsersWrite,
  ])
    ? "benutzer"
    : undefined;
  const invitesRoute = userPermissions.includes(Permission.InvitesRead)
    ? "einladungen"
    : undefined;
  const accountingRoute = userPermissions.includes(
    Permission.EndbestaendeSettingsWrite,
  )
    ? "mitglieder"
    : undefined;

  const closeMenu = () => {
    !matchesMedia && setSidebarOpen(false);
  };

  useEffect(closeMenu, [matchesMedia]);

  if (!auth.session) {
    return null;
  }

  return isValidating ? (
    <div className={styles.loadingSpinner}>
      <LoadingSpinner />
    </div>
  ) : (
    <>
      <header className={styles.header}>
        <div className={styles.aside}>
          <Hamburger
            isOpen={isSidebarOpen}
            className={styles.hamburger}
            onClick={() => setSidebarOpen(!isSidebarOpen)}
          />
          <Link to="/admin" className={styles.logo}>
            {i18n.admin.title}
          </Link>
        </div>
        <AppMenu />
        <AccountButton />
      </header>
      <div>
        <Sidebar isOpen={isSidebarOpen} close={closeMenu}>
          <Nav>
            {usersRoute && (
              <Nav.Item
                text={i18n.admin.users.title}
                to={usersRoute}
                icon={SVGPeople}
                onClick={closeMenu}
              />
            )}
            {invitesRoute && (
              <Nav.Item
                text={i18n.admin.invites.title}
                to={invitesRoute}
                icon={SVGOutbox}
                onClick={closeMenu}
              />
            )}
            {accountingRoute && (
              <Nav.Group title={i18n.departments.accounting.title}>
                <Nav.Item
                  text={i18n.admin.accounting.title}
                  to={accountingRoute}
                  onClick={closeMenu}
                />
              </Nav.Group>
            )}
          </Nav>
        </Sidebar>
        <Routes>
          {usersRoute && (
            <Route path={usersRoute}>
              <Route index element={<AdminUsers />} />
              <Route path=":userId/*" element={<AdminUser />} />
            </Route>
          )}
          {invitesRoute && (
            <Route path={invitesRoute} element={<AdminInvites />} />
          )}
          {accountingRoute && (
            <Route path={accountingRoute} element={<AdminAccounting />} />
          )}

          <Route
            path="*"
            element={
              <Navigate
                to={usersRoute ?? invitesRoute ?? accountingRoute ?? "/"}
                replace
              />
            }
          />
        </Routes>
      </div>
    </>
  );
};

export default AdminApp;
